import { computed, ref } from 'vue'
import { createGlobalState, useAsyncState } from '@vueuse/core'
import type { UserDTO } from '@/types/user'
import type { ActivityPreviewDTO, ReadVipMemberDTO } from '@/types/info'
import { getIndexInfoAgg } from '@/api/info'
import { apiUserGet } from '@/api/user'
import { appReadVipMember } from '@/api/vip'

const createDefaultPreview = (): ActivityPreviewDTO => ({
  claimed: 0,
  claimedSummary: {},
  rebatePreviewStatus: false
})

/**
 * 全局共享-用户信息
 */
export const useUserInfo = createGlobalState(() => {
  // === 状态 ===
  const rawUserInfo = ref<UserDTO | null>(null)
  const rawReadVipMember = ref<ReadVipMemberDTO | null>(null)
  const rawPreview = ref<ActivityPreviewDTO>(createDefaultPreview())

  // 对外导出：注入用户随机头像
  const userInfo = computed(() => {
    if (!rawUserInfo.value) return null
    const info = rawUserInfo.value
    // const username = (info.username || '').trim()
    // const avatar = info.avatar
    //   ? info.avatar
    //   : `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(username)}`
    return { ...info }
  })
  const readVipMember = computed(() => rawReadVipMember.value)
  // 红点信息
  const preview = computed(() => rawPreview.value)

  // === 操作 === 本地设置/部分更新/清空
  const setUserInfo = (data: UserDTO) => {
    rawUserInfo.value = data
  }
  const patchUserInfo = (patch: Partial<UserDTO>) => {
    rawUserInfo.value = { ...(rawUserInfo.value as UserDTO), ...patch }
  }
  const setPreview = (data: Partial<ActivityPreviewDTO>) => {
    rawPreview.value = {
      ...rawPreview.value,
      ...data
    }
  }
  const clearUserInfo = () => {
    rawUserInfo.value = null
    rawReadVipMember.value = null
    rawPreview.value = createDefaultPreview()
  }

  // 接口：初始化用户聚合信息
  const fetchInfoAgg = async () => {
    const res = await getIndexInfoAgg()
    const data = res?.data
    if (data?.userBase) {
      rawUserInfo.value = data.userBase
    }
    if (data?.readVipMember) {
      rawReadVipMember.value = data.readVipMember
    }
    if (data?.preview) {
      setPreview(data.preview)
    }
    return data ?? null
  }

  // 接口：查询基础用户信息
  const fetchUserInfo = async () => {
    const res = await apiUserGet()
    const data = res?.data
    if (data) rawUserInfo.value = data
    return data ?? null
  }

  // 接口：查询VIP会员信息 （暂时没用到
  const fetchVipMember = async () => {
    const res = await appReadVipMember()
    const data = res?.data
    if (data) rawReadVipMember.value = data
    return data ?? null
  }

  // 状态：获取用户信息中/错误
  const {
    isLoading: isInfoAggLoading,
    error: infoAggError,
    execute: initUserInfo
  } = useAsyncState(fetchInfoAgg, null, {
    immediate: false
  })
  const {
    isLoading: isUserInfoLoading,
    error: userInfoError,
    execute: refreshUserInfo
  } = useAsyncState(fetchUserInfo, null, {
    immediate: false
  })
  const {
    isLoading: isVipMemberLoading,
    error: vipMemberError,
    execute: refreshVipMember
  } = useAsyncState(fetchVipMember, null, {
    immediate: false
  })

  const isLoading = computed(
    () => isInfoAggLoading.value || isUserInfoLoading.value || isVipMemberLoading.value
  )
  const error = computed(() => infoAggError.value || userInfoError.value || vipMemberError.value)

  // === 计算属性 === 想用的 可以导出
  // const username = computed(() => userInfo.value?.username ?? '')
  // const avatar = computed(() => userInfo.value?.avatar ?? '')
  // const mobile = computed(() => userInfo.value?.mobile ?? '')
  // const isInnerMember = computed(() => userInfo.value?.isInnerMember ?? false)
  // const hasWithdrawPassword = computed(
  //   () => userInfo.value?.withdrawPassword && userInfo.value.withdrawPassword !== ''
  // )
  const isAuthorizedUser = computed(() => {
    return (
      userInfo.value &&
      Number.isFinite(userInfo.value?.registerType) &&
      userInfo.value.registerType > 0
    )
  })
  return {
    // 状态：用户信息
    userInfo,
    // 状态：VIP会员信息
    readVipMember,
    // 状态：优惠预览红点信息
    preview,
    // 状态：获取用户信息中
    isLoading,
    // 状态：错误
    error,
    // 操作：设置用户信息
    setUserInfo,
    // 操作：部分更新用户信息
    patchUserInfo,
    // 操作：设置优惠预览红点信息
    setPreview,
    // 操作：清空用户信息
    clearUserInfo,
    // 操作：初始化用户聚合信息
    initUserInfo,
    // 操作：刷新用户信息
    refreshUserInfo,
    // 操作：刷新VIP会员信息
    refreshVipMember,
    // 是否是三方联合登录的账号
    isAuthorizedUser
  }
})

export default useUserInfo
